<template>
  <transition name="drawer-fade">
    <div v-if="tableStore.isCartOpen" class="cart-overlay" @click="tableStore.toggleCart()">
      <div class="cart-drawer" @click.stop>
        <div class="cart-header">
          <h2>{{ $t('yourOrder') }}</h2>
          <button @click="tableStore.toggleCart()" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="cart-body" v-if="tableStore.cartItems.length > 0">
          <div class="cart-items">
            <div v-for="(group, userName) in groupedItems" :key="userName" class="user-group">
              <div class="user-header">
                <div class="user-avatar">{{ getInitials(userName) }}</div>
                <span class="user-name">{{ userName }}</span>
              </div>
              
              <div v-for="item in group" :key="item.id" class="cart-item">
                <div class="item-details">
                  <div class="item-header">
                    <h3>{{ item.name }}</h3>
                    <div class="price-info">
                      <span class="base-price">{{ $t('basePrice') }}: {{ formatPrice(getBasePrice(item)) }}</span>
                      <span class="total-price">{{ formatPrice(item.price) }}</span>
                    </div>
                  </div>
                  <p class="item-variant" v-if="item.customizations?.size">
                    {{ $t(item.customizations.size.nameKey) || item.customizations.size.id }}
                    <span v-if="item.customizations.size.priceModifier > 0">
                      (+ {{ formatPrice(item.customizations.size.priceModifier) }})
                    </span>
                  </p>
                  <div class="item-extras" v-if="hasExtras(item)">
                    <span v-if="item.customizations?.cheese">
                      + {{ $t(item.customizations.cheese.nameKey) || item.customizations.cheese.id }}
                      <template v-if="item.customizations.cheese.priceModifier > 0">
                        ({{ formatPrice(item.customizations.cheese.priceModifier) }})
                      </template>
                    </span>
                    <template v-if="item.customizations?.sauces">
                      <span v-for="sauce in item.customizations.sauces" :key="sauce.id || sauce">
                        + {{ $t(sauce.nameKey || '') || sauce.id || sauce }}
                        <template v-if="sauce.priceModifier > 0">
                          ({{ formatPrice(sauce.priceModifier) }})
                        </template>
                      </span>
                    </template>
                    <template v-if="item.customizations?.addIngredients">
                      <span v-for="ing in item.customizations.addIngredients" :key="ing.id || ing">
                        + {{ getOptionName(ing) }}
                        <template v-if="ing.price > 0">
                          ({{ formatPrice(ing.price) }})
                        </template>
                      </span>
                    </template>
                    <template v-if="item.customizations?.removeIngredients">
                      <span v-for="ing in item.customizations.removeIngredients" :key="ing.id || ing" class="removed-ingredient">
                        − {{ getOptionName(ing) }}
                      </span>
                    </template>
                  </div>
                </div>

                <div class="item-actions">
                  <div class="quantity-controls">
                    <button @click="tableStore.updateQuantity(item.id, item.quantity - 1)" class="qty-btn">−</button>
                    <span class="qty-val">{{ item.quantity }}</span>
                    <button @click="tableStore.updateQuantity(item.id, item.quantity + 1)" class="qty-btn">+</button>
                  </div>
                  <button @click="tableStore.removeItem(item.id)" class="remove-btn">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="empty-state" v-else>
          <div class="empty-icon">🛒</div>
          <h3>{{ $t('emptyCart') }}</h3>
          <p>{{ $t('cartIsEmptySubtitle') }}</p>
          <button @click="tableStore.toggleCart()" class="btn-secondary">
            {{ $t('browseMenu') }}
          </button>
        </div>

        <div class="cart-footer" v-if="tableStore.cartItems.length > 0">
          <div class="total-row">
            <span>{{ $t('total') }}</span>
            <span class="total-amount">{{ formatPrice(tableStore.cartTotal) }}</span>
          </div>
          <button @click="goToCheckout" class="btn-primary checkout-btn">
            {{ $t('proceedToCheckout') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useTableStore } from '@/stores/table'
import { useSessionStore } from '@/stores/session'
import { useMenuStore } from '@/stores/menu'
import { formatPrice } from '@/utils/formatters'

const router = useRouter()
const tableStore = useTableStore()
const sessionStore = useSessionStore()
const menuStore = useMenuStore()

const groupedItems = computed(() => {
  const groups = {}
  tableStore.cartItems.forEach(item => {
    const name = item.addedByName || 'Guest'
    if (!groups[name]) {
      groups[name] = []
    }
    groups[name].push(item)
  })
  return groups
})

const getInitials = (name) => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const getBasePrice = (item) => {
  const product = menuStore.getItemById(item.productId)
  return product ? product.price : 0
}

const hasExtras = (item) => {
  const c = item.customizations
  if (!c) return false
  return (
    c.cheese || 
    (c.sauces && c.sauces.length > 0) || 
    (c.addIngredients && c.addIngredients.length > 0) ||
    (c.removeIngredients && c.removeIngredients.length > 0)
  )
}

/**
 * Get display name for an option
 * For API data: use name directly
 * For static data: try i18n, fallback to name/id
 */
const getOptionName = (option) => {
  if (menuStore.isFromApi && option.name) {
    return option.name
  }
  return option.nameKey || option.name || option.id
}

const goToCheckout = () => {
  const slug = sessionStore.restaurantSlug || 'demo'
  tableStore.toggleCart()
  router.push(`/${slug}/checkout`)
}
</script>

<style scoped>
.cart-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(2px);
  z-index: 100;
  display: flex;
  justify-content: flex-end;
}

.cart-drawer {
  width: 100%;
  max-width: 400px;
  background: var(--surface-color);
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-lg);
  animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideIn {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

.cart-header {
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--surface-color);
}

.cart-header h2 {
  font-size: 1.25rem;
  margin: 0;
  color: var(--secondary-color);
}

.close-btn {
  background: #F1F5F9;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: var(--text-light);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #E2E8F0;
  color: var(--secondary-color);
}

.cart-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.cart-item {
  background: white;
  border-radius: var(--radius-md);
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
}

.item-header {
  display: flex;
  color: var(--text-light);
  margin-bottom: 4px;
}

.item-extras {
  font-size: 0.8rem;
  color: var(--text-light);
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.item-extras span {
  background: #F8FAFC;
  padding: 2px 6px;
  border-radius: 4px;
}

.item-extras .removed-ingredient {
  background: #FEE2E2;
  color: #DC2626;
}

.item-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #F8FAFC;
  padding: 4px;
  border-radius: 8px;
}

.qty-btn {
  width: 28px;
  height: 28px;
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: var(--secondary-color);
  transition: all 0.2s;
}

.qty-btn:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.qty-val {
  font-weight: 600;
  min-width: 20px;
  text-align: center;
}

.remove-btn {
  color: #EF4444;
  background: rgba(239, 68, 68, 0.1);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.remove-btn:hover {
  background: #EF4444;
  color: white;
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h3 {
  color: var(--secondary-color);
  margin-bottom: 8px;
}

.empty-state p {
  color: var(--text-light);
  margin-bottom: 24px;
}

.btn-secondary {
  background: white;
  border: 2px solid var(--border-color);
  padding: 12px 24px;
  border-radius: var(--radius-md);
  font-weight: 600;
  color: var(--secondary-color);
  transition: all 0.2s;
}

.btn-secondary:hover {
  border-color: var(--secondary-color);
}

.cart-footer {
  padding: 20px;
  background: white;
  border-top: 1px solid var(--border-color);
  box-shadow: 0 -4px 20px rgba(0,0,0,0.05);
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--secondary-color);
}

.total-amount {
  color: var(--primary-color);
}

/* Transitions */
.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.3s ease;
}

.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
}

.user-group {
  margin-bottom: 24px;
}

.user-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding-left: 4px;
}

.user-avatar {
  width: 32px;
  height: 32px;
  background: var(--primary-color);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
}

.user-name {
  font-weight: 600;
  color: var(--secondary-color);
  font-size: 0.95rem;
}
</style>
